import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ArrowRight, XIcon } from "lucide-react";
import { type FC, type FormEvent, useEffect, useRef } from "react";
import { colors, fonts, radius, spacing } from "../design-tokens";
import { ParagraphRegular } from "./typographies";
import { ActionButton } from "./action-button";

const Container = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm}px;
  background-color: ${colors.background};
  border: 1px solid ${colors.softPurple};
  border-radius: ${radius.md}px;
  padding: ${spacing.sm}px;
  transition: border-color 200ms ease-in-out;
  width: 100%;
  box-sizing: border-box;

  ${({ $error }) =>
    $error &&
    css`
      border-color: ${colors.danger};
    `}
`;

const Input = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  padding: ${spacing.sm}px ${spacing.md}px;
  font-size: 16px;
  font-weight: 500;
  color: ${colors.text};
  ${fonts.bodyFont}
  min-width: 0;

  &::placeholder {
    color: ${colors.primary};
    opacity: 0.7;
    font-weight: 500;
  }

  &:focus {
    outline: none;
  }
`;

const DisplayText = styled(ParagraphRegular)`
  flex: 1;
  padding: ${spacing.sm}px ${spacing.md}px;
  color: ${colors.text};
`;

const ErrorText = styled(ParagraphRegular)`
  color: ${colors.danger};
`;

const IconButton = styled.button`
  color: ${colors.background};
  background-color: ${colors.primary};
  border-radius: 50%;
  border: none;
  padding: ${spacing.sm}px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 200ms ease-in-out;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background-color: ${colors.primaryHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

type QueryInputProps = {
  value: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  error?: string | null;
};

export const QueryInput: FC<QueryInputProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder = "Ask anything...",
  disabled = false,
  autoFocus = false,
  error,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [autoFocus]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (value.trim() && onSubmit) {
      onSubmit(value.trim());
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange("");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Container $error={!!error}>
        <Input
          ref={inputRef}
          name="query-input"
          type="text"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
        />
        <ActionButton
          type="button"
          disabled={disabled || !value.trim()}
          size="small"
          variant="ghost"
          onClick={handleClear}
        >
          <XIcon size={16} color={colors.textTertiary} />
        </ActionButton>
        <IconButton type="submit" disabled={disabled || !value.trim()}>
          <ArrowRight size={24} />
        </IconButton>
      </Container>
      {error && <ErrorText>{error}</ErrorText>}
    </form>
  );
};
